import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, RefreshCw, FileText, Eye, X } from 'lucide-react';

const TAX_TYPES = ['GST', 'IGST', 'EXEMPT'];
const PO_STATUSES = [
  { value: 'ACTIVE', label: 'Active', color: 'bg-blue-100 text-blue-800' },
  { value: 'PARTIALLY_FULFILLED', label: 'Partial', color: 'bg-amber-100 text-amber-800' },
  { value: 'FULFILLED', label: 'Fulfilled', color: 'bg-green-100 text-green-800' },
  { value: 'CLOSED', label: 'Closed', color: 'bg-slate-100 text-slate-800' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

const CustomerPurchaseOrders = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [parts, setParts] = useState([]);
  const [partsInventory, setPartsInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [poDetails, setPoDetails] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewingPo, setViewingPo] = useState(null);
  const [customerParts, setCustomerParts] = useState([]);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'DESPATCH']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const posRes = await api.get('/customer-pos');
      setData(posRes.data);
      setLoading(false);
      const [custRes, partsRes, invRes] = await Promise.all([
        api.get('/customers'),
        api.get('/parts'),
        api.get('/parts/inventory-check')
      ]);
      setCustomers(custRes.data);
      setParts(partsRes.data);
      setPartsInventory(invRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getCustName = (id) => {
    const c = customers.find(x => x.id === id);
    return c ? `${c.code} - ${c.name}` : '-';
  };
  const getPartName = (id) => {
    const p = parts.find(x => x.id === id);
    return p ? `${p.part_no} - ${p.name}` : `ID:${id}`;
  };
  const getPartPrice = (partId) => {
    const inv = partsInventory.find(p => p.id === partId);
    return inv ? inv.sale_price : 0;
  };
  const getPartTaxRate = (partId) => {
    const part = parts.find(p => p.id === partId);
    return part && part.tax_rate ? parseFloat(part.tax_rate) : 0;
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        po_number: item.po_number || '',
        po_date: item.po_date || '',
        customer_id: (item.customer_id ? item.customer_id.toString() : ''),
        delivery_date: item.delivery_date || '',
        bill_to_address: item.bill_to_address || '',
        ship_to_address: item.ship_to_address || '',
        tax_type: item.tax_type || 'GST',
        remarks: item.remarks || '',
      });
      // #1: Load existing details for editing
      setPoDetails((item.details || []).map(d => ({
        part_id: (d.part_id ? d.part_id.toString() : ''),
        quantity: d.quantity || 0,
        unit_price: d.unit_price || 0,
        tax_percent: d.tax_percent || 0,
        tax_amount: d.tax_amount || 0,
        total_price: d.total_price || 0,
      })) || []);
      // #3: Load customer-specific parts
      if (item.customer_id) fetchCustomerParts(item.customer_id);
    } else {
      setEditingItem(null);
      setFormData({
        po_number: '', po_date: new Date().toISOString().split('T')[0],
        customer_id: '', delivery_date: '', bill_to_address: '', ship_to_address: '',
        tax_type: 'GST', remarks: '',
      });
      setPoDetails([]);
    }
    setIsDialogOpen(true);
  };

  const handleCustomerChange = async (cid) => {
    const c = customers.find(x => x.id === parseInt(cid));
    setFormData({
      ...formData, customer_id: cid,
      bill_to_address: (c && c.address) || '',
      ship_to_address: (c && c.address) || '',
    });
    // #3: Fetch parts for this customer
    await fetchCustomerParts(parseInt(cid));
    setPoDetails([]);
  };

  const fetchCustomerParts = async (customerId) => {
    try {
      const res = await api.get(`/parts/by-customer/${customerId}`);
      setCustomerParts(res.data);
    } catch {
      setCustomerParts([]);
    }
  };

  // Use customer-specific parts if available, else fallback to all parts
  const availableParts = customerParts.length > 0 ? customerParts : parts.filter(p => p.is_active);

  const handleAddPart = () => {
    setPoDetails([...poDetails, { part_id: '', quantity: 0, unit_price: 0, tax_percent: 0, tax_amount: 0, total_price: 0 }]);
  };

  const recalcDetail = (detail) => {
    const qty = parseFloat(detail.quantity) || 0;
    const price = parseFloat(detail.unit_price) || 0;
    const taxPct = parseFloat(detail.tax_percent) || 0;
    const subtotal = qty * price;
    const taxAmt = subtotal * taxPct / 100;
    return { ...detail, tax_amount: taxAmt, total_price: subtotal + taxAmt };
  };

  const handleDetailChange = (index, field, value) => {
    const updated = [...poDetails];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'part_id' && value) {
      updated[index].unit_price = getPartPrice(parseInt(value));
      updated[index].tax_percent = getPartTaxRate(parseInt(value));
    }
    updated[index] = recalcDetail(updated[index]);
    setPoDetails(updated);
  };

  const handleRemovePart = (i) => setPoDetails(poDetails.filter((_, idx) => idx !== i));
  const calcSubtotal = () => poDetails.reduce((s, d) => s + (parseFloat(d.unit_price) || 0) * (parseFloat(d.quantity) || 0), 0);
  const calcTax = () => poDetails.reduce((s, d) => s + (parseFloat(d.tax_amount) || 0), 0);
  const calcTotal = () => calcSubtotal() + calcTax();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.po_number || !formData.po_date) {
      toast.error('Customer, PO Number, and PO Date are required');
      return;
    }
    if (!editingItem && (poDetails.length === 0 || !poDetails.some(d => d.part_id && d.quantity))) {
      toast.error('At least one part is required');
      return;
    }
    setIsSubmitting(true);
    try {
      const submitData = {
        po_number: formData.po_number,
        po_date: formData.po_date,
        customer_id: parseInt(formData.customer_id),
        delivery_date: formData.delivery_date || null,
        bill_to_address: formData.bill_to_address || null,
        ship_to_address: formData.ship_to_address || null,
        tax_type: formData.tax_type || 'GST',
        remarks: formData.remarks || null,
        subtotal: calcSubtotal(),
        tax_amount: calcTax(),
        total_amount: calcTotal(),
        details: poDetails.filter(d => d.part_id && d.quantity).map(d => ({
          part_id: parseInt(d.part_id),
          quantity: parseFloat(d.quantity),
          unit_price: parseFloat(d.unit_price),
          tax_percent: parseFloat(d.tax_percent) || 0,
          tax_amount: parseFloat(d.tax_amount) || 0,
          total_price: parseFloat(d.total_price),
        })),
      };
      if (editingItem) {
        const { details, ...updateData } = submitData;
        await api.put(`/customer-pos/${editingItem.id}`, updateData);
        // #1: Also update details if they were modified
        if (poDetails.length > 0) {
          const detailsPayload = poDetails.filter(d => d.part_id && d.quantity).map(d => ({
            part_id: parseInt(d.part_id),
            quantity: parseFloat(d.quantity),
            unit_price: parseFloat(d.unit_price),
            tax_percent: parseFloat(d.tax_percent) || 0,
            tax_amount: parseFloat(d.tax_amount) || 0,
            total_price: parseFloat(d.total_price),
          }));
          await api.put(`/customer-pos/${editingItem.id}/details`, detailsPayload);
        }
        toast.success('PO updated');
      } else {
        await api.post('/customer-pos', submitData);
        toast.success('Customer PO created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this Customer PO?')) return;
    try {
      await api.delete(`/customer-pos/${item.id}`);
      toast.success('PO deleted');
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Delete failed');
    }
  };

  const handleView = async (item) => {
    try {
      const res = await api.get(`/customer-pos/${item.id}`);
      setViewingPo(res.data);
      setIsViewOpen(true);
    } catch { toast.error('Failed to load PO details'); }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await api.put(`/customer-pos/${item.id}`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
      setIsViewOpen(false);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const cfg = PO_STATUSES.find(s => s.value === status) || PO_STATUSES[0];
    return <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
  };

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (item.po_number || '').toLowerCase().includes(t) ||
      getCustName(item.customer_id).toLowerCase().includes(t) ||
      (item.status || '').toLowerCase().includes(t) ||
      (item.po_no ? item.po_no.toString() : '').includes(t);
  });

  const fmtCurrency = (v) => parseFloat(v || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="customer-pos">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <FileText className="w-6 h-6 text-sky-500" />
            Customer Purchase Orders
          </h1>
          <p className="text-slate-500 text-sm">{data.length} purchase orders</p>
        </div>
        {canEdit && (
          <Button onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-new-btn">
            <Plus className="w-4 h-4 mr-2" /> New Customer PO
          </Button>
        )}
      </div>

      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search PO number, customer, status..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-sm" data-testid="search-input" />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No customer POs found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>CPO No</TableHead>
                    <TableHead>PO Number</TableHead>
                    <TableHead>PO Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Delivery Date</TableHead>
                    <TableHead>Tax Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono font-bold">CPO-{item.po_no}</TableCell>
                      <TableCell className="font-medium">{item.po_number}</TableCell>
                      <TableCell>{new Date(item.po_date).toLocaleDateString()}</TableCell>
                      <TableCell className="text-sm">{getCustName(item.customer_id)}</TableCell>
                      <TableCell>{item.delivery_date ? new Date(item.delivery_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{item.tax_type}</TableCell>
                      <TableCell className="text-right font-mono">{fmtCurrency(item.total_amount)}</TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleView(item)} className="h-8 w-8" data-testid={`view-po-${item.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEdit && item.status === 'ACTIVE' && (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)} className="h-8 w-8" data-testid={`edit-po-${item.id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} className="h-8 w-8 text-red-600" data-testid={`delete-po-${item.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? `Edit CPO-${editingItem.po_no}` : 'New Customer Purchase Order'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4">
              <div>
                <Label className="form-label">PO Number <span className="text-red-500">*</span></Label>
                <Input value={formData.po_number} onChange={(e) => setFormData(prev => ({ ...prev, po_number: e.target.value }))}
                  placeholder="Customer's PO#" required className="rounded-sm" data-testid="po-number-input" />
              </div>
              <div>
                <Label className="form-label">PO Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={formData.po_date} onChange={(e) => setFormData(prev => ({ ...prev, po_date: e.target.value }))}
                  required className="rounded-sm" data-testid="po-date-input" />
              </div>
              <div>
                <Label className="form-label">Customer <span className="text-red-500">*</span></Label>
                <Select value={formData.customer_id} onValueChange={handleCustomerChange} disabled={!!editingItem}>
                  <SelectTrigger className="rounded-sm" data-testid="customer-select"><SelectValue placeholder="Select Customer" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {customers.filter(c => c.is_active).map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.code} - {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Delivery Date</Label>
                <Input type="date" value={formData.delivery_date} onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
                  className="rounded-sm" data-testid="delivery-date-input" />
              </div>
              <div>
                <Label className="form-label">Tax Type</Label>
                <Select value={formData.tax_type} onValueChange={(v) => setFormData(prev => ({ ...prev, tax_type: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="tax-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TAX_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Bill To Address</Label>
                <Textarea value={formData.bill_to_address} onChange={(e) => setFormData(prev => ({ ...prev, bill_to_address: e.target.value }))}
                  placeholder="Billing address" className="rounded-sm h-16" data-testid="bill-to-input" />
              </div>
              <div>
                <Label className="form-label">Ship To Address</Label>
                <Textarea value={formData.ship_to_address} onChange={(e) => setFormData(prev => ({ ...prev, ship_to_address: e.target.value }))}
                  placeholder="Shipping address" className="rounded-sm h-16" data-testid="ship-to-input" />
              </div>
              <div>
                <Label className="form-label">Remarks</Label>
                <Input value={formData.remarks} onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Notes" className="rounded-sm" data-testid="remarks-input" />
              </div>
            </div>

            {(
              <div className="mt-2 border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900">PO Items</h3>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddPart} className="rounded-sm" data-testid="add-part-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Part
                  </Button>
                </div>
                {customerParts.length > 0 && (
                  <p className="text-xs text-blue-600 mb-2">Showing {customerParts.length} parts associated with this customer</p>
                )}
                {poDetails.length === 0 ? (
                  <div className="text-center py-6 text-slate-500 border border-dashed rounded-sm">Click "Add Part" to add items.</div>
                ) : (
                  <div className="border rounded-sm overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Part</TableHead>
                          <TableHead>Qty</TableHead>
                          <TableHead>Unit Price</TableHead>
                          <TableHead>Tax %</TableHead>
                          <TableHead>Tax Amt</TableHead>
                          <TableHead>Total</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {poDetails.map((d, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <Select value={d.part_id} onValueChange={(v) => handleDetailChange(i, 'part_id', v)}>
                                <SelectTrigger className="rounded-sm w-48" data-testid={`part-select-${i}`}><SelectValue placeholder="Select Part" /></SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {availableParts.map(p => (
                                    <SelectItem key={p.id} value={p.id.toString()}>{p.part_no} - {p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input type="number" value={d.quantity} onChange={(e) => handleDetailChange(i, 'quantity', e.target.value)}
                                className="rounded-sm w-20" data-testid={`qty-input-${i}`} />
                            </TableCell>
                            <TableCell>
                              <Input type="number" step="0.01" value={d.unit_price} onChange={(e) => handleDetailChange(i, 'unit_price', e.target.value)}
                                className="rounded-sm w-28" data-testid={`price-input-${i}`} />
                            </TableCell>
                            <TableCell>
                              <Input type="number" step="0.01" value={d.tax_percent} onChange={(e) => handleDetailChange(i, 'tax_percent', e.target.value)}
                                className="rounded-sm w-20" data-testid={`tax-pct-input-${i}`} />
                            </TableCell>
                            <TableCell className="font-mono text-sm">{fmtCurrency(d.tax_amount)}</TableCell>
                            <TableCell className="font-mono font-medium">{fmtCurrency(d.total_price)}</TableCell>
                            <TableCell>
                              <Button type="button" variant="ghost" size="icon" onClick={() => handleRemovePart(i)} className="text-red-600" data-testid={`remove-part-${i}`}>
                                <X className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50">
                          <TableCell colSpan={4} className="text-right font-semibold">Subtotal:</TableCell>
                          <TableCell className="font-mono">{fmtCurrency(calcTax())}</TableCell>
                          <TableCell className="font-mono font-bold text-lg">{fmtCurrency(calcTotal())}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="submit-po-btn">
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create PO'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>CPO-{(viewingPo && viewingPo.po_no)} | {(viewingPo && viewingPo.po_number)}</span>
              {viewingPo && getStatusBadge(viewingPo.status)}
            </DialogTitle>
          </DialogHeader>
          {viewingPo && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><span className="text-sm text-slate-500">PO Date</span><p className="font-medium">{new Date(viewingPo.po_date).toLocaleDateString()}</p></div>
                <div><span className="text-sm text-slate-500">Customer</span><p className="font-medium">{getCustName(viewingPo.customer_id)}</p></div>
                <div><span className="text-sm text-slate-500">Delivery Date</span><p className="font-medium">{viewingPo.delivery_date ? new Date(viewingPo.delivery_date).toLocaleDateString() : '-'}</p></div>
                <div><span className="text-sm text-slate-500">Tax Type</span><p className="font-medium">{viewingPo.tax_type}</p></div>
                <div><span className="text-sm text-slate-500">Total Amount</span><p className="font-mono font-bold text-lg">{fmtCurrency(viewingPo.total_amount)}</p></div>
                {viewingPo.received_date && <div><span className="text-sm text-slate-500">Received Date</span><p className="font-medium">{new Date(viewingPo.received_date).toLocaleDateString()}</p></div>}
                {viewingPo.closed_date && <div><span className="text-sm text-slate-500">Closed Date</span><p className="font-medium">{new Date(viewingPo.closed_date).toLocaleDateString()}</p></div>}
              </div>
              <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-sm">
                <div><span className="text-sm text-slate-500">Bill To</span><p className="text-sm">{viewingPo.bill_to_address || '-'}</p></div>
                <div><span className="text-sm text-slate-500">Ship To</span><p className="text-sm">{viewingPo.ship_to_address || '-'}</p></div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">PO Items ({(viewingPo.details ? viewingPo.details.length : 0) || 0})</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Part</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Tax %</TableHead>
                      <TableHead className="text-right">Tax Amt</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Fulfilled</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(viewingPo.details || []).map((d, idx) => {
                      const pending = (parseFloat(d.quantity) || 0) - (parseFloat(d.fulfilled_quantity) || 0);
                      return (
                        <TableRow key={idx}>
                          <TableCell>{getPartName(d.part_id)}</TableCell>
                          <TableCell className="text-right font-mono">{parseFloat(d.quantity).toFixed(0)}</TableCell>
                          <TableCell className="text-right font-mono">{fmtCurrency(d.unit_price)}</TableCell>
                          <TableCell className="text-right">{parseFloat(d.tax_percent || 0).toFixed(2)}%</TableCell>
                          <TableCell className="text-right font-mono">{fmtCurrency(d.tax_amount)}</TableCell>
                          <TableCell className="text-right font-mono">{fmtCurrency(d.total_price)}</TableCell>
                          <TableCell className="text-right font-mono text-green-600">{parseFloat(d.fulfilled_quantity || 0).toFixed(0)}</TableCell>
                          <TableCell className="text-right font-mono text-orange-600">{pending.toFixed(0)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {viewingPo.remarks && (
                <div className="border-t pt-3"><h4 className="font-semibold mb-1">Remarks</h4><p className="text-slate-600">{viewingPo.remarks}</p></div>
              )}
              {canEdit && !['CANCELLED', 'CLOSED'].includes(viewingPo.status) && (
                <div className="border-t pt-4 flex gap-2">
                  {viewingPo.status === 'FULFILLED' && (
                    <Button onClick={() => handleStatusChange(viewingPo, 'CLOSED')} className="bg-slate-700 hover:bg-slate-800 rounded-sm" data-testid="close-po-btn">
                      Close PO
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => handleStatusChange(viewingPo, 'CANCELLED')} className="text-red-600 border-red-300 hover:bg-red-50 rounded-sm" data-testid="cancel-po-btn">
                    Cancel PO
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPurchaseOrders;
